/* PLTX2 Video Carousel — reference-parity slider on our own micro-engine.
   No third-party library: Pltx2Flick below reimplements the classic
   attraction/friction spring integrator (a published, standard technique;
   constants are documented public API values) for our narrow case:
   equal-width cells, wrapAround, center align, drag + flick, RTL,
   no arrows/dots/autoplay.

   Physics per frame (identical difference equation → identical motion):
     force = (target − x) · ATTRACTION
     v = (v + force) · (1 − FRICTION)
     x += v
   While dragging, x tracks the pointer 1:1 and v carries the last frame's
   delta, so a release flicks with natural momentum. Projected resting
   position x + v/FRICTION picks the landing cell; a fast last-instant
   flick boosts one cell in the flick direction.

   Wrapper behavior (cloned from the reference, unchanged):
   - Only the SELECTED video plays, and only while the strip is on screen
     (one IntersectionObserver, threshold 0.3); slide change pauses all
     and plays the new selected cell. Adjacent videos pre-buffer.
   - Post-resize settle pass re-measures with height transitions off.
   No sound controls — exercise preview videos are silent, always muted. */
(() => {
  const ATTRACTION = 0.025;
  const FRICTION = 0.28;
  const STATIC_CLICK_MOVE = 3; // px — under this, a tap is a click
  const FLICK_WINDOW = 100; // ms — last-move recency for boost

  class Pltx2Flick {
    constructor(wrapper, options) {
      this.wrapper = wrapper;
      this.rtl = !!options.rightToLeft;
      this.onChange = options.onChange || (() => {});
      this.onStaticClick = options.onStaticClick || (() => {});
      this.cells = [...wrapper.children];
      this.selectedIndex = -1; // so the initial select(0) applies classes
      this.x = 0;
      this.v = 0;
      this.target = 0;
      this.isDragging = false;
      this.#build();
      // class first: pre-init CSS hides cells until the engine owns layout,
      // and hidden elements measure as 0
      wrapper.classList.add('pltx2-flick-on');
      this.measure();
      this.select(0, true);
      this.#bind();
      // paint synchronously — the rAF loop only takes over for motion, so
      // there is no unstyled first frame (and background tabs, where rAF
      // is frozen, still get a correct static layout)
      this.#render();
      // measure()/select() already woke the loop — this is just a safety net
      this.#wake();
    }

    get selectedElement() {
      return this.cells[this.selectedIndex] || null;
    }

    #raf = null;
    #running = false;
    #cellShift = [];
    #sliderTx = null;
    #pointerId = null;
    #pointerStartX = 0;
    #dragStartX = 0;
    #pointerX = 0;
    #maxMove = 0;
    #lastMoveDelta = 0;
    #lastMoveTime = 0;
    #idle = false;

    #build() {
      const doc = this.wrapper.ownerDocument;
      this.viewport = doc.createElement('div');
      this.viewport.className = 'pltx2-flick-viewport';
      this.slider = doc.createElement('div');
      this.slider.className = 'pltx2-flick-slider';
      for (const cell of this.cells) {
        cell.style.position = 'absolute';
        cell.style.top = '0';
        cell.style[this.rtl ? 'right' : 'left'] = '0';
        this.slider.append(cell);
      }
      this.viewport.append(this.slider);
      this.wrapper.append(this.viewport);
      this.wrapper.tabIndex = 0;
    }

    /* cell geometry: positions include margins (outer widths), centers are
       used both for the wrap shift and the per-cell attractor */
    measure() {
      this.viewportW = this.viewport.offsetWidth || this.wrapper.offsetWidth;
      this.positions = [];
      this.centers = [];
      let cursor = 0;
      let maxH = 0;
      for (const cell of this.cells) {
        const cs = getComputedStyle(cell);
        const ml = parseFloat(cs.marginLeft) || 0;
        const mr = parseFloat(cs.marginRight) || 0;
        // rect, not offset*: keeps fractional sizes exact (367.5, 217.5)
        const rect = cell.getBoundingClientRect();
        this.positions.push(cursor);
        this.centers.push(cursor + ml + rect.width / 2);
        cursor += ml + rect.width + mr;
        maxH = Math.max(maxH, rect.height);
      }
      this.total = cursor;
      this.viewport.style.height = maxH + 'px';
      // geometry moved, so every cached transform is stale
      this.#cellShift.length = 0;
      this.#sliderTx = null;
      this.#wake();
    }

    /* attractor for cell i: x that centers it, in the wrap representation
       nearest to `near` */
    #attractor(index, near) {
      const base = this.centers[index] - this.viewportW / 2;
      return base + this.total * Math.round((near - base) / this.total);
    }

    #setSelected(index) {
      index = ((index % this.cells.length) + this.cells.length) % this.cells.length;
      if (index === this.selectedIndex) return false;
      this.selectedIndex = index;
      for (let i = 0; i < this.cells.length; i++) {
        this.cells[i].classList.toggle('is-selected', i === index);
        this.cells[i].setAttribute('aria-hidden', i === index ? 'false' : 'true');
      }
      this.onChange();
      return true;
    }

    select(index, instant) {
      this.#setSelected(index);
      this.target = this.#attractor(this.selectedIndex, this.x);
      if (instant) {
        this.x = this.target;
        this.v = 0;
      }
      this.#wake();
    }

    resize() {
      this.measure();
      this.select(this.selectedIndex, true);
      this.#render();
    }

    destroy() {
      if (this.#raf) cancelAnimationFrame(this.#raf);
      this.#raf = null;
      this.#running = false;
      this.#unbind?.();
    }

    #bind() {
      const vp = this.viewport;
      const onDown = (event) => {
        if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
        this.#pointerId = event.pointerId;
        this.#pointerStartX = this.#pointerX = event.clientX;
        this.#dragStartX = this.x;
        this.#maxMove = 0;
        this.#lastMoveDelta = 0;
        this.#lastMoveTime = 0;
        this.isDragging = true;
        this.#wake(); // the loop parks itself once settled — restart it
        vp.classList.add('is-pointer-down');
        try { vp.setPointerCapture(event.pointerId); } catch (e) { /* pointer already gone */ }
      };
      const onMove = (event) => {
        if (event.pointerId !== this.#pointerId) return;
        const delta = event.clientX - this.#pointerX;
        this.#pointerX = event.clientX;
        this.#maxMove = Math.max(this.#maxMove, Math.abs(event.clientX - this.#pointerStartX));
        this.#lastMoveDelta = this.rtl ? -delta : delta;
        this.#lastMoveTime = event.timeStamp;
        if (this.#maxMove > STATIC_CLICK_MOVE) event.preventDefault();
      };
      const onUp = (event) => {
        if (event.pointerId !== this.#pointerId) return;
        this.#pointerId = null;
        this.isDragging = false;
        vp.classList.remove('is-pointer-down');
        if (event.type === 'pointerup' && this.#maxMove < STATIC_CLICK_MOVE) {
          // pointer capture retargets the event to the viewport — resolve
          // the tapped cell from coordinates instead of event.target
          const hit = document.elementFromPoint(event.clientX, event.clientY);
          const cellIndex = this.cells.findIndex((cell) => cell.contains(hit));
          if (cellIndex > -1) this.onStaticClick(cellIndex);
          this.target = this.#attractor(this.selectedIndex, this.x);
          return;
        }
        this.#settleFromDrag(event.timeStamp);
      };
      const onKey = (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        if (event.target !== this.wrapper) return;
        event.preventDefault();
        const forward = (event.key === 'ArrowRight') !== this.rtl;
        this.#selectAdjacent(forward ? 1 : -1);
      };
      vp.addEventListener('pointerdown', onDown);
      vp.addEventListener('pointermove', onMove);
      vp.addEventListener('pointerup', onUp);
      vp.addEventListener('pointercancel', onUp);
      this.wrapper.addEventListener('keydown', onKey);
      this.#unbind = () => {
        vp.removeEventListener('pointerdown', onDown);
        vp.removeEventListener('pointermove', onMove);
        vp.removeEventListener('pointerup', onUp);
        vp.removeEventListener('pointercancel', onUp);
        this.wrapper.removeEventListener('keydown', onKey);
      };
    }

    #unbind = null;

    /* move exactly one cell in the given logical direction */
    #selectAdjacent(direction) {
      const next = this.selectedIndex + direction;
      this.#setSelected(next);
      this.target = this.#attractor(this.selectedIndex, this.x + direction * 0.001);
      // make sure the pull goes the requested way even across the wrap seam
      if (direction > 0 && this.target < this.x) this.target += this.total;
      if (direction < 0 && this.target > this.x) this.target -= this.total;
      this.#wake();
    }

    /* release: project the resting position from current velocity, land on
       the nearest attractor; a fast trailing flick boosts one cell over */
    #settleFromDrag(releaseTime) {
      const restingX = this.x + this.v / FRICTION;
      let bestIndex = 0;
      let bestTarget = 0;
      let bestDist = Infinity;
      for (let i = 0; i < this.cells.length; i++) {
        const candidate = this.#attractor(i, restingX);
        const dist = Math.abs(candidate - restingX);
        if (dist < bestDist) {
          bestDist = dist;
          bestIndex = i;
          bestTarget = candidate;
        }
      }
      const flicked =
        releaseTime - this.#lastMoveTime < FLICK_WINDOW && Math.abs(this.#lastMoveDelta) > 1;
      if (bestIndex === this.selectedIndex && this.#maxMove > STATIC_CLICK_MOVE && flicked) {
        // drag direction: pointer moving toward inline-start advances x
        const direction = this.#lastMoveDelta < 0 ? 1 : -1;
        this.#selectAdjacent(direction);
        return;
      }
      this.#setSelected(bestIndex);
      this.target = bestTarget;
      this.#wake();
    }

    /* restart the loop if it parked — every state change that can move the
       strip funnels through here. Nothing visual toggles: compositor-layer
       churn at gesture boundaries measured WORSE on the sibling engine
       (see MISSION-15 §3). */
    #wake() {
      this.#idle = false;
      if (this.#running) return;
      this.#running = true;
      this.#raf = requestAnimationFrame(this.#tick);
    }

    #tick = () => {
      if (this.isDragging) {
        const dxScreen = this.#pointerX - this.#pointerStartX;
        const newX = this.#dragStartX - (this.rtl ? -dxScreen : dxScreen);
        this.v = newX - this.x;
        this.x = newX;
        this.#render();
      } else if (!this.#idle) {
        const force = (this.target - this.x) * ATTRACTION;
        this.v = (this.v + force) * (1 - FRICTION);
        this.x += this.v;
        if (Math.abs(this.target - this.x) < 0.4 && Math.abs(this.v) < 0.4) {
          this.x = this.target;
          this.v = 0;
          this.#idle = true;
        }
        this.#render();
      } else {
        /* settled: park the loop. The old version re-armed forever, waking
           the main thread every ~16ms for the life of the page with nothing
           moving. Parking is purely internal — no styles change here. */
        this.#running = false;
        this.#raf = null;
        return;
      }
      this.#raf = requestAnimationFrame(this.#tick);
    };

    /* The slider carries the motion: ONE transform write per frame moves the
       whole strip on a single compositor layer (will-change lives in the
       stylesheet). Cells re-position only when their wrap representation
       changes, which is rare and happens at least a viewport off-screen.
       Ported from the before/after copy of this engine, where it measured
       median 36.4ms vs 42.4ms against the old 10-writes-a-frame loop. */
    #render() {
      // 1/64px keeps sub-pixel smoothness without toFixed's per-frame string
      const sx = Math.round((this.rtl ? this.x : -this.x) * 64) / 64;
      if (sx !== this.#sliderTx) {
        this.#sliderTx = sx;
        this.slider.style.transform = 'translate3d(' + sx + 'px,0,0)';
      }
      for (let i = 0; i < this.cells.length; i++) {
        const rel = this.centers[i] - this.x;
        const shift = this.total * Math.round((this.viewportW / 2 - rel) / this.total);
        if (shift === this.#cellShift[i]) continue;
        this.#cellShift[i] = shift;
        const pos = this.positions[i] + shift;
        this.cells[i].style.transform = 'translate3d(' + (this.rtl ? -pos : pos) + 'px,0,0)';
      }
    }
  }

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  class Pltx2VideoCarousel extends HTMLElement {
    #engine = null;
    #io = null;
    #inView = false;
    #resizeTimer = null;
    #onResize = null;

    connectedCallback() {
      this.wrapper = this.querySelector('.pltx2-vc__wrapper');
      this.videos = [...this.querySelectorAll('.pltx2-vc__video')];
      if (!this.wrapper || this.wrapper.classList.contains('pltx2-flick-on')) return;

      // The muted content attribute is unreliable for autoplay policy — set the property.
      for (const video of this.videos) video.muted = true;

      this.#watchLoading();

      const rtl = (this.closest('[dir]')?.getAttribute('dir') || document.documentElement.getAttribute('dir')) === 'rtl';

      this.#engine = new Pltx2Flick(this.wrapper, {
        rightToLeft: rtl,
        onChange: () => this.#playSelected(),
        onStaticClick: (cellIndex) => {
          this.#engine.select(cellIndex);
          this.#playSelected();
        }
      });

      /* settle pass: re-measure once with height transitions suppressed so
         the viewport snaps to exact target heights after a breakpoint flip */
      this.#onResize = () => {
        if (this.#resizeTimer) clearTimeout(this.#resizeTimer);
        this.#resizeTimer = setTimeout(() => {
          this.classList.add('pltx2-vc--no-anim');
          this.#engine?.resize();
          requestAnimationFrame(() => this.classList.remove('pltx2-vc--no-anim'));
        }, 200);
      };
      window.addEventListener('resize', this.#onResize);

      this.#io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            this.#inView = entry.isIntersecting;
            // also gates the shimmer sweep (see pltx2-vc--offscreen in the CSS)
            this.classList.toggle('pltx2-vc--offscreen', !entry.isIntersecting);
            if (entry.isIntersecting) this.#playSelected();
            else for (const video of this.videos) video.pause();
          }
        },
        { threshold: 0.3 }
      );
      this.#io.observe(this);

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', (event) => {
          if (!this.contains(event.target)) return;
          const index = this.#engine.cells.indexOf(event.target);
          if (index > -1) this.#engine.select(index);
        });
      }
    }

    disconnectedCallback() {
      this.#io?.disconnect();
      this.#io = null;
      if (this.#onResize) window.removeEventListener('resize', this.#onResize);
      if (this.#resizeTimer) clearTimeout(this.#resizeTimer);
      this.#engine?.destroy();
      this.#engine = null;
      for (const video of this.videos || []) video.pause();
    }

    /* Loading skin. Cells ship preload="none" (posters cover the visuals —
       frame-cut assets for CDN URLs, preview_image for picker videos), and
       the shimmer bridges whatever gap remains. Hold it until the browser
       actually has a frame to show (readyState >= HAVE_CURRENT_DATA), and
       drop it on error so a dead source never shimmers forever. */
    #watchLoading() {
      for (const video of this.videos) {
        const media = video.closest('.pltx2-vc__media');
        if (!media) continue;
        const clear = () => media.classList.remove('is-loading');
        if (video.readyState >= 2) {
          clear();
          continue;
        }
        video.addEventListener('loadeddata', clear, { once: true });
        video.addEventListener('error', clear, { once: true });
      }
    }

    #playSelected() {
      for (const video of this.videos) video.pause();
      const selected = this.#engine?.selectedElement?.querySelector('.pltx2-vc__video');
      if (!selected) return;
      if (this.#inView && !REDUCED.matches) {
        selected.play().catch(() => {});
        this.#warmNeighbors();
      }
    }

    /* Start buffering the wrap-adjacent videos so a swipe plays instantly
       (markup ships preload=none so offscreen strips cost nothing) */
    #warmNeighbors() {
      const cells = this.#engine?.cells;
      if (!cells || cells.length < 2) return;
      const index = this.#engine.selectedIndex;
      for (const offset of [1, -1]) {
        const cell = cells[(index + offset + cells.length) % cells.length];
        const video = cell?.querySelector('.pltx2-vc__video');
        if (video && video.preload === 'none') {
          video.preload = 'auto';
          video.load();
        }
      }
    }
  }

  if (!customElements.get('pltx2-video-carousel')) {
    customElements.define('pltx2-video-carousel', Pltx2VideoCarousel);
  }
})();
